<template>
  <v-card
    class="c-card d-flex flex-column pa-4"
    :class="{ 'c-draft': isDraft, 'c-wide': wide }"
  >
    <!-- avatar -->
    <PersonAvatar
      class="ma-auto"
      clickable
      colour="secondary"
      :person="person"
      :size="avatarSize"
      @click="revealBack = !bio"
    />

    <!-- headline -->
    <div class="c-headline text-center">
      <v-card-title
        class="justify-center pa-0 mt-2"
      >
        {{ displayName }}
      </v-card-title>
      <v-card-subtitle v-if="suffixes" class="text-center mt-0 pa-0">
        {{ personSuffixes }}
      </v-card-subtitle>
      <v-card-text
        v-if="responsibility && person.responsibility"
        class="text-center font-weight-regular py-2 pb-0"
      >
        {{ person.responsibility && person.responsibility[locale] }}
      </v-card-text>
      <v-card-text
        v-if="headline && person.headline"
        class="text-center font-weight-black py-2 pb-0"
      >
        {{ person.headline && person.headline[locale] }}
      </v-card-text>
    </div>

    <v-card-text v-if="bio && localeBio">
      <RichText :blocks="localeBio" />
    </v-card-text>

    <v-spacer />

    <!-- actions -->
    <v-card-actions v-if="contact && showContactInfo" class="py-5">
      <v-spacer />
      <v-btn icon @click=";`mailto:${person.email}`">
        <v-icon color="accent" size="60"> mdi-email </v-icon>
      </v-btn>
      <v-spacer />
      <v-btn icon :href="person.linkedIn">
        <v-icon color="#0072b1" size="60"> mdi-linkedin </v-icon>
      </v-btn>
      <v-spacer />
    </v-card-actions>

    <!-- flip card -->
    <v-scroll-x-reverse-transition>
      <PersonCardBack v-model="revealBack" :person="person" />
    </v-scroll-x-reverse-transition>
  </v-card>
</template>

<script>
import PersonAvatar from '@/components/base/PersonAvatar'
import PersonCardBack from '@/components/person/PersonCardBack'
import RichText from '@/components/base/RichText'

export default {
  name: 'PersonCard',

  components: {
    PersonAvatar,
    PersonCardBack,
    RichText
  },

  props: {
    person: {
      type: Object,
      required: true
    },

    /* profile elements */

    bio: {
      type: Boolean,
      required: false,
      default: false
    },

    contact: {
      type: Boolean,
      required: false,
      default: false
    },

    responsibility: {
      type: Boolean,
      required: false,
      default: false
    },

    headline: {
      type: Boolean,
      required: false,
      default: false
    },

    suffixes: {
      type: Boolean,
      required: false,
      default: false
    },

    /* dimensions */

    size: {
      type: Number,
      required: false,
      default: 0
    },

    wide: {
      type: Boolean,
      required: false,
      default: false
    }
  },

  data: function () {
    return {
      id: this.person.id,
      revealBack: false
    }
  },

  computed: {
    avatarSize() {
      const sizes = {
        xs: 150,
        sm: 150,
        md: 150,
        lg: 200,
        xl: 250
      }
      return this.size || sizes[this.$vuetify.breakpoint.name] || 200
    },

    hasAvatar() {
      return !!this.person.avatar
    },

    isDark() {
      return this.$store.state.themeStore.isDark
    },

    isDraft() {
      return this.person.id.startsWith('drafts.')
    },

    locale() {
      return this.$store.state.i18nStore.locale
    },

    localeBio() {
      return this.person.bio?.[this.locale]
    },

    displayName() {
      const name = this.person.displayName || `${this.person.firstName} ${this.person.lastName}`
      const honorificName = this.person.honorific
        ? `${this.person.honorific} ${this.person.displayName}`
        : name
      return this.suffixes ? name : honorificName
    },

    personSuffixes() {
      return this.person.suffixes?.join(', ') || ''
    },

    showContactInfo() {
      return this.$feature('contactInfo')
    }
  },

  methods: {
    emailPerson(_person) {},

    callPerson(_person) {},

    textPerson(_person) {},

    zoomPerson(_person) {}
  }
}
</script>

<style lang="scss" scoped>
.c-avatar {
  cursor: pointer;
}

.c-card {
  margin: auto;
  height: 100%;
  min-width: 275px;
  max-width: min(400px, 100%);
  border-radius: 16px;

  &.c-wide {
    max-width: 100%;
  }

  &.c-draft {
    opacity: 0.6;
    border: solid 8px var(--v-accent-base);
  }

  .v-card__title {
    font-weight: bold;
    font-size: 1.5rem;
  }

  .v-card__subtitle {
    font-size: 1.2rem;
  }

  .v-card__text {
    font-size: 1.2rem;
    line-height: 1.5;
  }
}
</style>
